import React from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select as MUISelect,
  SelectProps as MUISelectProps,
  SelectChangeEvent,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { Option } from '@common/types';
import { pick } from 'lodash';

interface CustomMultiSelectProps extends Omit<
  MUISelectProps<string[]>,
  'onChange'
> {
  name: string;
  label?: string;
  onChange: (value: string[]) => void;
  clearable?: boolean;
  clearLabel?: string;
  record_id?: string;
  options: Option[];
  error?: boolean;
  value?: string[];
  helperText?: string;
}

const renderInputValues = (
  options: { label: string; value: string }[],
  values: string[],
) => {
  if (values.length === 0 || values.length === options.length) {
    return 'All';
  }

  return options
    .filter(option => values.includes(option.value))
    .map(option => option.label)
    .join(', ');
};

export const MultiSelect = React.forwardRef<
  HTMLDivElement,
  CustomMultiSelectProps
>(
  (
    {
      name,
      label,
      onChange,
      error = false,
      options = [],
      value,
      helperText,
      sx,
      ...props
    },
    ref,
  ) => {
    return (
      <FormControl
        fullWidth
        sx={{
          ...sx,
        }}
        {...pick(props, 'size', 'required', 'variant')}
      >
        <InputLabel shrink={!!value || value === ''} error={error}>
          {label}
        </InputLabel>
        <MUISelect
          inputRef={ref}
          ref={ref}
          value={value}
          name={name}
          label={label}
          variant='outlined'
          error={error}
          fullWidth
          onChange={(event: SelectChangeEvent<string[]>) =>
            onChange(event.target.value as string[])
          }
          endAdornment={
            <InputAdornment position='end'>
              {renderInputValues(options, value || []) !== 'All' && (
                <IconButton sx={{ mr: 1 }} onClick={() => onChange([])}>
                  <Clear sx={{ width: '16px', height: '16px' }} />
                </IconButton>
              )}
            </InputAdornment>
          }
          displayEmpty
          renderValue={(values: string[]) => renderInputValues(options, values)}
          {...props}
        >
          {options.map((option, index) => (
            <MenuItem
              key={`${name}-${option.value}-${index}`}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </MUISelect>
        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}
      </FormControl>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';
